import { observable, action } from 'mobx';
import { db } from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import * as toastr from 'toastr';
import history from '../components/history';

// =====================================
// CONSTS
import { CustomerSort } from '../const/CustomerSort';
import { Messages } from '../const/Messages';

// =====================================
// STORES
import { commonStore } from './CommonStore';

// =====================================
// MODELS
import { Customer } from '../models/Customer';
import { NewContractedCustomer } from '../models/NewContractedCustomer';
import { User } from '../models/User';
import { DeliveryRule } from '../models/DeliveryRule';
import { Article } from '../models/Article';
import { NewContractedCustomerLocation } from '../models/NewContractedCustomerLocation';

export interface ICustomerStore {
    customers: Array<Customer>;
    helperCustomerslist: Array<Customer>;
    articlesPricelist: Array<Article>;
    helperPricelist: Array<Article>;
    newContractedCustomer: NewContractedCustomer;
    fromHour: any;
    toHour: any;
    keyword: string;
    customerSorting: string;
    lastVisible: any;
    customersIdArray: Array<string>;

    isLoading: boolean;
    isSearchHeaderVisible: boolean;
    isCustomersEmpty: boolean;
    isBtnSaveDisabled: boolean;
    isSearchEmpty: boolean;
    isDrawerVisible: boolean;
    isDebtInfoVisible: boolean;
    //hasMoreCustomersToLoad: boolean;
    //hasMoreArticlesToLoad: boolean;

    fullNameMessage: string;
    contactPersonMessage: string;
    taxIdMessage: string;
    registrationIdMessage: string;
    phoneNumberMessage: string;
    emailMessage: string;
    deliveryAddressMessage: string;
    fromHourMessage: string;
    toHourMessage: string;
    paymentTermsMessage: string;
    addressMessage: string;
    cityMessage: string;
    postalCodeMessage: string;

    init(): void;
    //getNextCustomers(): void;
    getPricelist(): void;
    //fetchMorePricelist(): void;
    handleOpenDrawer(): void;
    handleCloseDrawer(): void;
    handleChangeDebtVisibility(event: any): void;
    handleChangeCustomerSorting(event: any): void;
    showSearchHeaderForPricelist(): void;
    hideSearchHeader(): void;
    clearArticleSearch(): void;
    handleArticleSearch(event: any);
    onHandleChangeInput(event: any): void;
    showSearchHeaderForCustomers(): void;
    handleCustomersSearch(event: any);
    onHandleChangeFromDate(date: any): void;
    onHandleChangeToDate(date: any): void;
    // addNewLocationRow(): void;
    // handleChangeLocation(event: any);
    // deteteLocationRow(index: number): void;
    saveNewContractedCustomer(event: any);
    reset(): void;
}

export class CustomerStore implements ICustomerStore {
    constructor() {
        this.customers = [];
        this.helperCustomerslist = [];
        this.articlesPricelist = [];
        this.helperPricelist = [];
        this.newContractedCustomer = new NewContractedCustomer();
        this.fromHour = null;
        this.toHour = null;
        this.keyword = "";
        this.customerSorting = "";
        this.lastVisible = null;
        this.customersIdArray = [];

        this.isLoading = true;
        this.isSearchHeaderVisible = false;
        this.isCustomersEmpty = true;
        this.isBtnSaveDisabled = false;
        this.isSearchEmpty = false;
        this.isDrawerVisible = false;
        this.isDebtInfoVisible = false;
        //this.hasMoreCustomersToLoad = true;
        //this.hasMoreArticlesToLoad = true;

        this.fullNameMessage = "";
        this.contactPersonMessage = "";
        this.taxIdMessage = "";
        this.registrationIdMessage = "";
        this.phoneNumberMessage = "";
        this.emailMessage = "";
        this.deliveryAddressMessage = "";
        this.fromHourMessage = "";
        this.toHourMessage = "";
        this.paymentTermsMessage = "";
        this.addressMessage = "";
        this.cityMessage = "";
        this.postalCodeMessage = "";
    }

    @observable customers: Array<Customer>;
    @observable helperCustomerslist: Array<Customer>;
    @observable articlesPricelist: Array<Article>;
    @observable helperPricelist: Array<Article>;
    @observable newContractedCustomer: NewContractedCustomer;
    @observable fromHour: any;
    @observable toHour: any;
    @observable keyword: string;
    @observable customerSorting: string;
    @observable lastVisible: any;
    customersIdArray: Array<string>;

    @observable isLoading: boolean;
    @observable isSearchHeaderVisible: boolean;
    @observable isCustomersEmpty: boolean;
    @observable isBtnSaveDisabled: boolean;
    @observable isSearchEmpty: boolean;
    @observable isDrawerVisible: boolean;
    @observable isDebtInfoVisible: boolean;
    //@observable hasMoreCustomersToLoad: boolean;
    //@observable hasMoreArticlesToLoad: boolean;

    @observable fullNameMessage: string;
    @observable contactPersonMessage: string;
    @observable taxIdMessage: string;
    @observable registrationIdMessage: string;
    @observable phoneNumberMessage: string;
    @observable emailMessage: string;
    @observable deliveryAddressMessage: string;
    @observable fromHourMessage: string;
    @observable toHourMessage: string;
    @observable paymentTermsMessage: string;
    @observable addressMessage: string;
    @observable cityMessage: string;
    @observable postalCodeMessage: string;

    @action init = (): void => {
        let currentUser: User = commonStore.user;

        if (currentUser.salesRepMatchId.length > 0 && currentUser.salesRepMatchId[0] === "admin") {
            this.getCustomersForAdmin();
        } else {
            this.getCustomers();
        }
    }

    @action getCustomers = async () => {
        try {
            let currentUser: User = commonStore.user;

            if (currentUser.salesRepMatchId.length > 0) {

                let response = await Promise.all(currentUser.salesRepMatchId.map(async (item) => {
                    return await db.collection("organizations")
                        .doc(currentUser.orgId)
                        .collection('sales_reps')
                        .doc(item)
                        .get();
                }));

                response.forEach((querySalesReps) => {
                    if (querySalesReps.exists) {
                        // querySalesReps.data().customers.forEach((item) => {
                        //     this.customersIdArray.push(item.id);
                        // })
                        this.customersIdArray.push(querySalesReps.data().externalReferenceCode); // ToDo Promeniti ime u externalReferenceCodeArray
                    }
                })

                await this.fetchMoreCustomers();

            } else {
                this.isLoading = false;
            }
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting customers: ", error);
        };
    }

    @action getCustomersForAdmin = async () => {
        try {
            let currentUser: User = commonStore.user;

            const querySnapshotCustomers = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .where('isInactive', '==', false)
                .orderBy("accountsReceivables", "desc")
                .limit(10)
                .get();

            if (!querySnapshotCustomers.empty) {
                this.lastVisible = querySnapshotCustomers.docs[querySnapshotCustomers.docs.length - 1];
                querySnapshotCustomers.forEach((doc) => {
                    this.customers = [...this.customers, doc.data() as any];
                    this.isCustomersEmpty = false;
                });
            }

            this.isLoading = false;
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting customers: ", error);
        };
    }

    // @action getNextCustomers = (): void => {
    //     let currentUser: User = commonStore.user;

    //     if (currentUser.salesRepMatchId.length > 0 && currentUser.salesRepMatchId[0] === "admin") {
    //         this.fetchMoreCustomersForAdmin();
    //     } else {
    //         this.fetchMoreCustomers();
    //     }
    // }

    @action fetchMoreCustomers = async () => {
        let currentUser: User = commonStore.user;

        // if (this.lastVisible === null) {
        //     this.lastVisible = 0;
        // }

        // if (this.lastVisible >= this.customersIdArray.length) {
        //     this.hasMoreCustomersToLoad = false;
        // }

        // if (this.customersIdArray.length > 0) {
        //     this.customersIdArray.forEach(async (item, i) => {
        //         if (this.lastVisible <= i && i < (this.lastVisible + 10)) {
        //             const querySnapshotCustomers = await db.collection("organizations")
        //                 .doc(currentUser.orgId)
        //                 .collection('customers')
        //                 .doc(item)
        //                 .get();

        //             this.customers = [...this.customers, querySnapshotCustomers.data() as any];
        //             this.isCustomersEmpty = false;
        //             this.isLoading = false;
        //         }
        //     });

        //     this.lastVisible = this.lastVisible + 10;
        // } else {
        //     this.isLoading = false;
        // }

        const querySnapshotCustomers = await db.collection("organizations")
            .doc(currentUser.orgId)
            .collection('customers')
            .orderBy("accountsReceivables", "desc")
            .where('salesRep.externalReferenceCode', 'in', this.customersIdArray)
            .get();

        if (!querySnapshotCustomers.empty) {
            querySnapshotCustomers.forEach((doc) => {
                this.customers = [...this.customers, doc.data() as any];
                this.isCustomersEmpty = false;
            });
        }

        this.isLoading = false;
        //this.hasMoreCustomersToLoad = false;
    }

    @action fetchMoreCustomersForAdmin = async () => {
        try {
            let currentUser: User = commonStore.user;

            const querySnapshotCustomers = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .orderBy("accountsReceivables", "desc")
                .where('isInactive', '==', false)
                .startAfter(this.lastVisible)
                .limit(10)
                .get();

            if (!querySnapshotCustomers.empty) {
                this.lastVisible = querySnapshotCustomers.docs[querySnapshotCustomers.docs.length - 1];
                //this.hasMoreCustomersToLoad = true;
                querySnapshotCustomers.forEach((doc) => {
                    this.customers = [...this.customers, doc.data() as any];
                });
            } else {
                //this.hasMoreCustomersToLoad = false;
            }
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting customers: ", error);
        };
    }

    @action getPricelist = async () => {
        try {
            let currentUser: User = commonStore.user;

            const querySnapshot = await db.collection('catalogs')
                .doc(currentUser.orgId)
                .collection('articles')
                .where('isInactive', '==', false)
                .orderBy("code", "asc")
                .get();

            if (!querySnapshot.empty) {
                //this.lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
                querySnapshot.forEach((doc) => {
                    this.articlesPricelist = [...this.articlesPricelist, doc.data() as any];
                });
            }

            this.isLoading = false;
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting pricelist: ", error);
        };
    }

    // @action fetchMorePricelist = async () => {
    //     try {
    //         let currentUser: User = commonStore.user;

    //         const querySnapshot = await db.collection('catalogs')
    //             .doc(currentUser.orgId)
    //             .collection('articles')
    //             .orderBy("code", "asc")
    //             .startAfter(this.lastVisible)
    //             .limit(10)
    //             .get();

    //         if (!querySnapshot.empty) {
    //             this.lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    //             this.hasMoreArticlesToLoad = true;
    //             querySnapshot.forEach((doc) => {
    //                 this.articlesPricelist = [...this.articlesPricelist, doc.data() as any];
    //             });
    //         } else {
    //             this.hasMoreArticlesToLoad = false;
    //         }

    //         this.isLoading = false;
    //     }
    //     catch (error) {
    //         this.isLoading = false;
    //         console.error("Error getting pricelist: ", error);
    //     };
    // }

    @action handleOpenDrawer = (): void => {
        this.isDrawerVisible = true;
    }

    @action handleCloseDrawer = (): void => {
        this.isDrawerVisible = false;
    }

    @action handleChangeDebtVisibility = (event: any): void => {
        this.isDebtInfoVisible = event.target.checked;
    }

    @action handleChangeCustomerSorting = (event: any): void => {
        this.customerSorting = event.target.value;

        let customers: Array<Customer> = [...this.customers];

        if (this.customerSorting === CustomerSort.ALPHABETICALLY) {
            customers.sort((a, b) => (a.fullName > b.fullName) ? 1 : -1);
        } else if (this.customerSorting === CustomerSort.DEBTAMOUNT) {
            customers.sort((a, b) => (a.accountsReceivables < b.accountsReceivables) ? 1 : -1);
        }

        this.customers = customers;
    }

    @action showSearchHeaderForPricelist = (): void => {
        this.isSearchHeaderVisible = true;
        this.helperPricelist = this.articlesPricelist;
        this.articlesPricelist = [];
    }

    @action hideSearchHeader = (): void => {
        this.keyword = "";
        this.isSearchHeaderVisible = false;
        this.isSearchEmpty = false;
        this.articlesPricelist = this.helperPricelist;
        this.customers = this.helperCustomerslist;
    }

    @action clearArticleSearch = (): void => {
        this.keyword = "";
        this.isSearchEmpty = false;
        this.articlesPricelist = [];
        this.customers = [];
    }

    @action handleArticleSearch = async (event: any) => {
        this.keyword = event.target.value;
        this.isLoading = true;

        //let pricelistHelper: Array<Article> = [];
        // let currentUser: User = commonStore.user;
        // let articlesRef = db.collection('catalogs')
        //     .doc(currentUser.orgId)
        //     .collection('articles');

        //if (this.keyword.length >= 3) {
        // const result = await articlesRef
        //     .where('code', '>=', this.keyword)
        //     .where('code', '<=', this.keyword + '\uf8ff')
        //     .get();

        // pricelistHelper = this.helperPricelist.filter(item => {
        //     return item.code.toLowerCase().includes(this.keyword.toLowerCase());
        // });

        // if (!result.empty) {
        //     result.forEach((doc) => {
        //         pricelistHelper = [...pricelistHelper, doc.data() as any];
        //     });
        //     this.isSearchEmpty = false;
        // } else {
        //     this.isSearchEmpty = true;
        // }
        //}

        let pricelistHelper: Array<Article> = this.helperPricelist.filter(item => {
            return item.code.toLowerCase().includes(this.keyword.toLowerCase()) ||
                item.name.toLowerCase().includes(this.keyword.toLowerCase());
        });

        //if (this.keyword.length >= 5) {
        // const result = await articlesRef
        //     .where('name', '>=', this.keyword)
        //     .where('name', '<=', this.keyword + '\uf8ff')
        //     .get();

        // pricelistHelper = this.helperPricelist.filter(item => {
        //     return item.name.toLowerCase().includes(this.keyword.toLowerCase());
        // });

        // if (!result.empty) {
        //     result.forEach((doc) => {
        //         pricelistHelper = [...pricelistHelper, doc.data() as any];
        //     });
        //     this.isSearchEmpty = false;
        // } else {
        //     this.isSearchEmpty = true;
        // }
        //}

        if (pricelistHelper.length > 0) {
            this.isSearchEmpty = false;
        } else {
            this.isSearchEmpty = true;
        }

        this.articlesPricelist = [...pricelistHelper];
        this.isLoading = false;
    }

    @action onHandleChangeInput = (event: any): void => {
        const { name, value } = event.target;
        this[`${name}Message`] = "";
        this.newContractedCustomer[name] = value;
    }

    @action showSearchHeaderForCustomers = (): void => {
        this.isSearchHeaderVisible = true;
        this.helperCustomerslist = this.customers;
        this.customers = [];
    }

    @action handleCustomersSearch = async (event: any) => {
        this.keyword = event.target.value;
        this.isLoading = true;

        let customersHelper: Array<Customer> = this.helperCustomerslist.filter(item => {
            return item.externalReferenceCode.toLowerCase().includes(this.keyword.toLowerCase()) ||
                item.fullName.toLowerCase().includes(this.keyword.toLowerCase());
        });

        if (customersHelper.length > 0) {
            this.isSearchEmpty = false;
        } else {
            this.isSearchEmpty = true;
        }

        this.customers = [...customersHelper];
        this.isLoading = false;
    }

    @action onHandleChangeFromDate = (date: any) => {
        this.fromHourMessage = "";
        this.toHourMessage = "";
        this.fromHour = date;
    }

    @action onHandleChangeToDate = (date: any) => {
        this.fromHourMessage = "";
        this.toHourMessage = "";
        this.toHour = date;
    }

    // @action addNewLocationRow = () => {
    //     let location: NewContractedCustomerLocation = {
    //         index: Math.random(),
    //         address: "",
    //         city: "",
    //         postalCode: ""
    //     };
    //     this.newContractedCustomer.locations.push(location);
    // }

    // @action handleChangeLocation = (event: any) => {
    //     console.log(+event.target.dataset.id);
    //     this.newContractedCustomer[+event.target.dataset.id][event.target.name] = event.target.value;
    // }

    // @action deteteLocationRow = (index: number) => {
    //     this.newContractedCustomer.locations.splice(index, 1);
    // }

    @action validateNewContractedCustomer = (): boolean => {
        let isValid: boolean = true;

        if (this.newContractedCustomer.fullName.trim() === "") {
            this.fullNameMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.contactPerson.trim() === "") {
            this.contactPersonMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.taxId === "") {
            this.taxIdMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.taxId !== "" && this.newContractedCustomer.taxId.length !== 9) {
            this.taxIdMessage = Messages.PIB_MAX_LENGTH;
            isValid = false;
        }

        if (this.newContractedCustomer.registrationId === "") {
            this.registrationIdMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.registrationId !== "" && this.newContractedCustomer.registrationId.length !== 8) {
            this.registrationIdMessage = Messages.MB_MAX_LENGTH;
            isValid = false;
        }

        if (this.newContractedCustomer.phoneNumber === "") {
            this.phoneNumberMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        let pattern: RegExp = /^[(]?[0-9]{2}[)]?[-\s\/]?[0-9]{3}[-\s]?[0-9]{4,6}$/;
        let checkPhoneNumber = pattern.test(this.newContractedCustomer.phoneNumber);
        if (this.newContractedCustomer.phoneNumber !== "" && !checkPhoneNumber) {
            this.phoneNumberMessage = Messages.INVALID_PHONENUMBER_FORMAT;
            isValid = false;
        }

        let emailPattern: RegExp = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        let checkEmail = emailPattern.test(this.newContractedCustomer.email);
        if (this.newContractedCustomer.email !== "" && !checkEmail) {
            this.emailMessage = Messages.INVALID_EMAIL_FORMAT;
            isValid = false;
        }

        if (this.newContractedCustomer.deliveryAddress.trim() === "") {
            this.deliveryAddressMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.fromHour !== null && this.toHour === null) {
            this.toHourMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.toHour !== null && this.fromHour === null) {
            this.fromHourMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.paymentTerms === "") {
            this.paymentTermsMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.address === "") {
            this.addressMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.city === "") {
            this.cityMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        if (this.newContractedCustomer.postalCode === "") {
            this.postalCodeMessage = Messages.REQUIRED_FIELD;
            isValid = false;
        }

        return isValid;
    }

    @action saveNewContractedCustomer = async (event: any) => {
        event.preventDefault();
        if (!this.validateNewContractedCustomer()) return;

        try {
            this.isBtnSaveDisabled = true;

            let id: string = uuidv4();
            let currentUser: User = commonStore.user;

            // let customerDeliveryRules = [];
            // if (this.fromHour !== null && this.toHour !== null) {
            //     let customerDeliveryRule: DeliveryRule = {
            //         fromHour: this.fromHour.format("HH:mm"),
            //         toHour: this.toHour.format("HH:mm")
            //     };

            //     customerDeliveryRules.push(customerDeliveryRule);
            // }

            let locations = [];

            let location: NewContractedCustomerLocation = {
                address: this.newContractedCustomer.address,
                city: this.newContractedCustomer.city,
                postalCode: this.newContractedCustomer.postalCode
            };

            locations.push(location);

            await db.collection('organizations')
                .doc(currentUser.orgId)
                .collection('new_customer_requests')
                .doc(id)
                .set({
                    id: id,
                    fullName: this.newContractedCustomer.fullName,
                    contactPerson: this.newContractedCustomer.contactPerson,
                    taxId: this.newContractedCustomer.taxId,
                    registrationId: this.newContractedCustomer.registrationId,
                    phoneNumber: this.newContractedCustomer.phoneNumber,
                    email: this.newContractedCustomer.email,
                    deliveryAddress: this.newContractedCustomer.deliveryAddress,
                    paymentTerms: this.newContractedCustomer.paymentTerms,
                    locations: locations,
                    orgSalesRepId: currentUser.id,
                    orgId: currentUser.orgId
                });

            toastr.success(Messages.SUCCESS_CUSTOMER);
            history.push('customers');
        }
        catch (error) {
            this.isBtnSaveDisabled = false;
            console.error("Error saving customer: ", error);
        };
    }

    @action reset = (): void => {
        this.customers = [];
        this.helperCustomerslist = [];
        this.articlesPricelist = [];
        this.helperPricelist = [];
        this.newContractedCustomer = new NewContractedCustomer();
        this.fromHour = null;
        this.toHour = null;
        this.keyword = "";
        this.customerSorting = "";
        this.lastVisible = null;
        this.customersIdArray = [];

        this.isLoading = true;
        this.isSearchHeaderVisible = false;
        this.isCustomersEmpty = true;
        this.isBtnSaveDisabled = false;
        this.isSearchEmpty = false;
        this.isDrawerVisible = false;
        this.isDebtInfoVisible = false;
        //this.hasMoreCustomersToLoad = true;
        //this.hasMoreArticlesToLoad = true;

        this.fullNameMessage = "";
        this.contactPersonMessage = "";
        this.taxIdMessage = "";
        this.registrationIdMessage = "";
        this.phoneNumberMessage = "";
        this.emailMessage = "";
        this.deliveryAddressMessage = "";
        this.fromHourMessage = "";
        this.toHourMessage = "";
        this.paymentTermsMessage = "";
        this.addressMessage = "";
        this.cityMessage = "";
        this.postalCodeMessage = "";
    }
}

export let customerStore = new CustomerStore();